import React, {useState} from "react";
import {Link} from "react-router";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {Document, Filters} from "../containers/data_assets_base";
import DocumentSelector from "./document_selector";
import ProjectSelector from "routes/searcher/components/settings/document_selector/project_selector";
import ContainedTextField from "routes/workflow/workflow_detail/components/contained_text_field";
import {Project, ProjectId} from "common/types/project";
import {OrganisationId} from "common/types/organisation";
import {DocumentId} from "common/types/document";
import {getQueryParams} from "routes/data_assets/data_assets_create_view/containers/data_assets_create_view";
import DateFilter from "common_components/flowmaster/llm_run/date_filter";
import Field from "common_components/flowmaster/llm_run/filtering/filter_field";

const styles = {
  wrapper: {
    padding: "1em 1em 0 1em",
  },
  title: {
    marginBottom: "16px",
    fontSize: "1.3rem",
    justifyContent: "space-between",
  },
  tabsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    borderBottom: "1px solid #e8e8e8",
  },
  tabsRoot: {
    minHeight: "40px",
    borderBottom: "none",
  },
  tabRoot: {
    textTransform: "none",
    minWidth: "120px",
    fontWeight: 400,
    marginRight: 8,
    color: "rgba(0, 0, 0, 0.7)",
    borderRadius: "4px 4px 0 0",
  },
  tabRootSelected: {
    backgroundColor: "#2196f3",
    color: "#fff",
    fontWeight: 500,
  },
  filtersFieldset: {
    position: "relative",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: "16px 0px 12px 12px",
    backgroundColor: "#fafafa",
    marginBottom: ".5em",
  },
  filtersLegend: {
    position: "absolute",
    top: "-8px",
    left: "10px",
    padding: "0 .5empx",
    backgroundColor: "#fafafa",
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.7)",
  },
  controlsContainer: {
    display: "flex",
    alignItems: "flex-end",
    marginBottom: ".5em",
    gap: "1em",
  },
  dateFilterContainer: {
    marginRight: "1em",
  },
  entitySelector: {
    minWidth: 150,
    marginRight: "1em",
  },
} as const;

interface DataAssetsTabsProps {
  currentPath: string;
  organisationId: OrganisationId;
  projectId: ProjectId | null;
  documents: Document[];
  location: {
    pathname: string;
    search: string;
  };
  params: {
    organisationId: string;
  };
  router: {
    push: (location: {pathname: string; search?: string}) => void;
    replace: (location: {pathname: string; search?: string}) => void;
  };
  projects: Project[];
  selectProject: (projectId: ProjectId | null) => void;
  documentId?: DocumentId | null;
  selectDocument: (documentId: DocumentId) => void;
  clearLlmRuns: () => void;
  filters: Filters;
  setFilters: (pairs: Partial<Filters>) => void;
  setDates: (dates: {from: string; to: string}) => void;
  maxRuns: number;
  setMaxRuns: (maxRuns: number) => void;
  autoRefreshEnabled: boolean;
  onAutoRefreshToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const DataAssetsTabs: React.FC<DataAssetsTabsProps> = props => {
  const {
    currentPath,
    organisationId,
    projectId,
    documents,
    projects,
    selectProject,
    documentId,
    selectDocument,
    clearLlmRuns,
    setFilters,
    filters,
    setDates,
    maxRuns,
    setMaxRuns,
    autoRefreshEnabled,
    onAutoRefreshToggle,
  } = props;

  const [entityType] = useState<"Document">("Document");

  const isDefinitionPath = currentPath.includes("/definition/");
  const activeTab = isDefinitionPath ? "create" : currentPath.split("/").pop();
  const basePath = currentPath.split("/data-assets")[0];

  const handleTabClick = (targetTab: string) => (e: React.MouseEvent) => {
    if (
      (targetTab === "overview" && activeTab === "overview") ||
      (targetTab === "create" && activeTab === "create")
    ) {
      e.preventDefault();
      return;
    }
    clearLlmRuns();
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.tabsContainer}>
        <div style={{display: "flex", flexDirection: "column"}}>
          <h1 style={styles.title}>Data Assets</h1>
          <Tabs
            value={activeTab === "overview" ? 0 : 1}
            style={styles.tabsRoot}
            TabIndicatorProps={{style: {display: "none"}}}
          >
            <Tab
              component={Link}
              to={`${basePath}/data-assets/overview${getQueryParams()}`}
              label="Overview"
              style={{
                ...styles.tabRoot,
                ...(activeTab === "overview" ? styles.tabRootSelected : {}),
              }}
              onClick={handleTabClick("overview")}
            />
            <Tab
              component={Link}
              to={`${basePath}/data-assets/create${getQueryParams()}`}
              label="Create/View"
              style={{
                ...styles.tabRoot,
                ...(activeTab === "create" || isDefinitionPath
                  ? styles.tabRootSelected
                  : {}),
              }}
              onClick={handleTabClick("create")}
            />
          </Tabs>
        </div>

        <div style={styles.filtersFieldset}>
          <div style={styles.filtersLegend}>Filters</div>
          <div style={styles.controlsContainer}>
            <div>
              <Field
                label="username"
                value={filters.user}
                updator={user => setFilters({user})}
                styles={{margin: 0}}
              />
              <ContainedTextField
                initialValue={maxRuns.toString()}
                label="Max Runs"
                type="number"
                onUpdate={value => {
                  const val = parseInt(value, 10);
                  if (val > 0) {
                    setMaxRuns(val);
                  }
                }}
                inputPropsStyle={{
                  width: "6em",
                }}
              />
            </div>
            <div style={styles.dateFilterContainer}>
              <DateFilter
                values={{from: filters.from, to: filters.to}}
                updateValue={setDates}
                column={true}
              />
            </div>

            {false && (
              <FormControl variant="outlined" style={styles.entitySelector}>
                <InputLabel id="entity-type-label">Entity Type</InputLabel>
                <Select
                  labelId="entity-type-label"
                  value={entityType}
                  label="Entity Type"
                >
                  <MenuItem value="Document">Document</MenuItem>
                </Select>
              </FormControl>
            )}
            <div
              style={{display: "flex", gap: projectId !== null ? "0em" : "1em"}}
            >
              <ProjectSelector
                organisationId={organisationId}
                projectId={projectId}
                projects={projects}
                selectProject={selectProject}
                autocompleteStyle={{width: "18em"}}
              />
              {entityType === "Document" && (
                <DocumentSelector
                  organisationId={organisationId}
                  projectId={projectId}
                  documents={documents}
                  documentId={documentId}
                  selectDocument={selectDocument}
                  autocompleteStyle={{width: "18em"}}
                />
              )}
            </div>
          </div>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoRefreshEnabled}
                  onChange={onAutoRefreshToggle}
                  color="primary"
                />
              }
              label="Auto refresh"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataAssetsTabs;
